import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { GlobalFunctions } from "@global/GlobalFunctions";
import { AuthenticationService, IWebAppUser } from "@services/authentication.service";
import { IOrganisation, IOrganisationsHierarchy, OrganisationDataService } from "@services/organisation-data.service";
import { siteSubscriptions } from "@services/siteSubscriptions";
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, Subject, take, takeUntil } from "rxjs";


@Component({
	selector: 'app-switch-accounts',
	templateUrl: './switch-accounts.component.html',
	styleUrl: './switch-accounts.component.scss'
})
export class SwitchAccountsComponent implements OnInit {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	currentUser: IWebAppUser;

	accountGUID: string;
	organisationUID: string;

	organisationHeirarchy: IOrganisationsHierarchy[];
	organisationHeirarchyFound: boolean = false;
	incorrectDetails: boolean = false;

	private loading: boolean = false;

	/**
	 * Default Constructor
	 * @param activatedRoute activatedRoute
	 * @param authenticationService authenticationService
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param dialog dialog
	 * @param accountsService accountsService
	 */
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private organisationDataService: OrganisationDataService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService
	) {
		// console.log('SwitchAccountsComponent CONSTRUCTOR');
	}

	/**
	   * Page On Initialise Event Handler
	   */
	ngOnInit(): void {

		// console.log('SwitchAccountsComponent ngOnInit', this);

		combineLatest([
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
		]).pipe(
			filter(([currentUser, queryParamMap]) =>
				!!currentUser && !!queryParamMap
			),
			take(1),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([currentUser, queryParamMap]) => {

				if (this.loading) {
					return;
				}

				this.loading = true;

				try {

					this.currentUser = currentUser

					// Get the GUID for the selected Account to Manage
					const accountGUID = queryParamMap.get('accountID') ?? "CDCE72D9-EA9C-4269-BFC9-45FA1913BDC4"
					const organisationUID = queryParamMap.get('organisationID') ?? "2F40D6E2-77DD-4080-AF92-5A17D59F4039"

					const myOrganisations = await this.getOrganisations(accountGUID, organisationUID)
					if (myOrganisations && myOrganisations.length > 0) {
						if (myOrganisations.some(o =>
							o.Uid.toLocaleLowerCase() == organisationUID.toLocaleLowerCase() &&
							o.AccountGUID?.toLocaleLowerCase() == accountGUID.toLocaleLowerCase())) {
							this.accountGUID = accountGUID
							this.organisationUID = organisationUID
							this.incorrectDetails = false;

							this.siteSubscriptions.SetSelectedSubLocation("");
							this.siteSubscriptions.SetSelectedAccountGUID(accountGUID);
							this.siteSubscriptions.SetSelectedOrganisationUID(organisationUID);

							this.organisationHeirarchy = await this.getOrganisationStructure(this.accountGUID, this.organisationUID);

							this.organisationHeirarchyFound = true;

							setTimeout(async () => {
								this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/dashboard`]);
							}, 1000);

						} else {
							this.incorrectDetails = true;
						}
					} else {
						this.incorrectDetails = true;
					}
				}
				catch (error: any) {
					this.globalFunctions.HandleError(error, "Error: combineLatest", false)
					this.incorrectDetails = true;
				} finally { }

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combineLatest", false);
				this.incorrectDetails = true;
			},
		})
	}

	/**
	 * Get the Organisations
	 * @param AccountID AccountID
	 * @param OrganisationID OrganisationID
	 * @returns 
	 */
	async getOrganisations(AccountID: string, OrganisationUID: string): Promise<IOrganisation[]> {

		try {

			// console.log("Awaiting:this.organisationDataService.getOrganisations$");

			const organisations = await firstValueFrom(
				this.organisationDataService.getOrganisations$(AccountID, OrganisationUID).pipe(
					filter((structure) => !!structure)
				)
			);

			// console.log("Done:this.organisationDataService.getOrganisations$");

			if (organisations) {
				/** Update the Organisation */
				this.siteSubscriptions.setmyOrganisations(organisations);
			}
			return organisations;

		} catch (error: any) {

			// console.error("Error:this.organisationDataService.getOrganisations$");

			// console.error(error); 
			if (error["status"] != 404) {
				this.globalFunctions.HandleError(error, "Error: this.organisationDataService.getOrganisations$", false)
			}
			return [];
		}
	}

	/**
	 * Get the Organisation Structure
	 * @param AccountID AccountID
	 * @param OrganisationID OrganisationID
	 * @returns 
	 */
	async getOrganisationStructure(AccountID: string, OrganisationUID: string): Promise<IOrganisationsHierarchy[]> {

		try {

			// console.log("Awaiting:this.organisationDataService.getOrganisationStructure$");

			const structure = await firstValueFrom(
				this.organisationDataService.getOrganisationStructure$(AccountID, OrganisationUID).pipe(
					filter((structure) => !!structure)
				)
			);

			// console.log("Done:this.organisationDataService.getOrganisationStructure$");
			/** Update the Organisation */
			let OrganisationStructure = JSON.parse(structure);
			this.siteSubscriptions.SethierarchicalData(OrganisationStructure);

			return OrganisationStructure;

		} catch (error: any) {

			// console.error("Error:this.organisationDataService.getOrganisationStructure$");

			// console.error(error); 
			if (error["status"] != 404) {
				this.globalFunctions.HandleError(error, "Error: this.organisationDataService.getOrganisationStructure$", false)
			}
			this.siteSubscriptions.SethierarchicalData([]);
			return [];
		}
	}


}
